import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost } from '../client';
import { qk } from './keys';
import type {
  PaymentIntent,
  PaymentIntentRequest,
  PaymentMethod,
  PaymentMethodInput,
} from '../types';

export function usePaymentMethods() {
  return useQuery({
    queryKey: qk.paymentMethods,
    queryFn: () => apiGet<PaymentMethod[]>('/me/payment-methods'),
  });
}

export function useAddPaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PaymentMethodInput) =>
      apiPost<PaymentMethod>('/me/payment-methods', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.paymentMethods });
    },
  });
}

export function useDeletePaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<void>(`/me/payment-methods/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.paymentMethods });
    },
  });
}

export function useCreatePaymentIntent() {
  return useMutation({
    mutationFn: (body: PaymentIntentRequest) => apiPost<PaymentIntent>('/payments/intents', body),
  });
}

export function usePaymentIntent(id: string | null) {
  return useQuery({
    queryKey: ['payments', 'intents', id ?? ''] as const,
    queryFn: () => apiGet<PaymentIntent>(`/payments/intents/${id ?? ''}`),
    enabled: !!id,
    refetchInterval: (q) => {
      const data = q.state.data as PaymentIntent | undefined;
      if (!data) return 3000;
      return data.status === 'open' ? 3000 : false;
    },
  });
}
